'use client';

import { c as _c } from "react/compiler-runtime";
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
import CircleResult from './CircleResult';
import { angleDelta, colorFor, FULL_TURN, meanOf, MIN_RADIUS, type Point, scoreOf } from './circleScoring';
const BEST = bestKey('circle');
type Status = 'idle' | 'drawing' | 'done' | 'tooShort';

/**
 * Pointer capture is what keeps the stroke alive when the cursor leaves the
 * canvas mid-sweep, but it throws if the pointer id is no longer active (a
 * cancelled touch, a synthesised event). Losing capture only costs us tracking
 * outside the box, so never let it take the whole stroke down with it.
 */
const capture = (element: HTMLCanvasElement, pointerId: number, mode: 'set' | 'release') => {
  try {
    if (mode === 'set') element.setPointerCapture(pointerId);else element.releasePointerCapture(pointerId);
  } catch {
    // Not capturable — carry on drawing without it.
  }
};
const PerfectCircle = () => {
  const $ = _c(32);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      width: 0,
      height: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const sizeRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pointsRef = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      total: 0,
      last: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sweepRef = useRef(t2);
  const drawingRef = useRef(false);
  const [status, setStatus] = useState("idle");
  const [accuracy, setAccuracy] = useState(null);
  const best = useLocalBest(BEST);
  const {
    record
  } = useGameTracking();
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const resize = () => {
        const dpr = window.devicePixelRatio || 1;
        const {
          width,
          height
        } = canvas.getBoundingClientRect();
        if (width === 0 || height === 0) {
          return;
        }
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return;
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 4;
        ctxRef.current = ctx;
        sizeRef.current = {
          width,
          height
        };
      };
      resize();
      const observer = new ResizeObserver(resize);
      observer.observe(canvas);
      return () => observer.disconnect();
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => ({
      x: sizeRef.current.width / 2,
      y: sizeRef.current.height / 2
    });
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const centre = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const ctx_0 = ctxRef.current;
      if (!ctx_0) {
        return;
      }
      ctx_0.clearRect(0, 0, sizeRef.current.width, sizeRef.current.height);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const clear = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const ctx_1 = ctxRef.current;
      const points = pointsRef.current;
      if (!ctx_1 || points.length < 2) {
        return;
      }
      clear();
      const mean = meanOf(points.map(_temp));
      const {
        x: cx,
        y: cy
      } = centre();
      ctx_1.save();
      ctx_1.setLineDash([5, 7]);
      ctx_1.lineWidth = 1.5;
      ctx_1.strokeStyle = "rgba(127,127,127,0.55)";
      ctx_1.beginPath();
      ctx_1.arc(cx, cy, mean, 0, Math.PI * 2);
      ctx_1.stroke();
      ctx_1.restore();
      for (let i = 1; i < points.length; i = i + 1, i) {
        const from = points[i - 1];
        const to = points[i];
        ctx_1.strokeStyle = colorFor(to.radius, mean);
        ctx_1.beginPath();
        ctx_1.moveTo(from.x, from.y);
        ctx_1.lineTo(to.x, to.y);
        ctx_1.stroke();
      }
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const paintScored = t7;
  let t8;
  if ($[8] !== record) {
    t8 = () => {
      drawingRef.current = false;
      const points_0 = pointsRef.current;
      const closedLoop = Math.abs(sweepRef.current.total) >= FULL_TURN;
      if (!closedLoop || points_0.length < 12) {
        setStatus("tooShort");
        setAccuracy(null);
        clear();
        return;
      }
      const score = scoreOf(points_0.map(_temp2));
      paintScored();
      setAccuracy(score);
      setStatus("done");
      record("circle", Number(score.toFixed(1)));
    };
    $[8] = record;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const finish = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event => {
      const ctx_2 = ctxRef.current;
      if (!ctx_2) {
        return;
      }
      capture(event.currentTarget, event.pointerId, "set");
      const {
        left,
        top
      } = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - left;
      const y = event.clientY - top;
      const {
        x: cx_0,
        y: cy_0
      } = centre();
      clear();
      pointsRef.current = [];
      sweepRef.current = {
        total: 0,
        last: Math.atan2(y - cy_0, x - cx_0)
      };
      const radius = Math.hypot(x - cx_0, y - cy_0);
      if (radius >= MIN_RADIUS) {
        pointsRef.current.push({
          x,
          y,
          radius
        });
      }
      drawingRef.current = true;
      setAccuracy(null);
      setStatus("drawing");
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const start = t9;
  let t10;
  if ($[11] !== finish) {
    t10 = event_0 => {
      const ctx_3 = ctxRef.current;
      if (!ctx_3 || !drawingRef.current) {
        return;
      }
      const {
        left: left_0,
        top: top_0
      } = event_0.currentTarget.getBoundingClientRect();
      const x_0 = event_0.clientX - left_0;
      const y_0 = event_0.clientY - top_0;
      const {
        x: cx_1,
        y: cy_1
      } = centre();
      const radius_0 = Math.hypot(x_0 - cx_1, y_0 - cy_1);
      if (radius_0 < MIN_RADIUS) {
        return;
      }
      const angle = Math.atan2(y_0 - cy_1, x_0 - cx_1);
      sweepRef.current = {
        total: sweepRef.current.total + angleDelta(sweepRef.current.last, angle),
        last: angle
      };
      const previous = pointsRef.current[pointsRef.current.length - 1];
      pointsRef.current.push({
        x: x_0,
        y: y_0,
        radius: radius_0
      });
      if (previous) {
        ctx_3.strokeStyle = "rgb(129 140 248)";
        ctx_3.beginPath();
        ctx_3.moveTo(previous.x, previous.y);
        ctx_3.lineTo(x_0, y_0);
        ctx_3.stroke();
      }
      if (Math.abs(sweepRef.current.total) >= Math.PI * 2) {
        capture(event_0.currentTarget, event_0.pointerId, "release");
        finish();
      }
    };
    $[11] = finish;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const move = t10;
  let t11;
  if ($[13] !== finish) {
    t11 = () => {
      if (!drawingRef.current) {
        return;
      }
      finish();
    };
    $[13] = finish;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const end = t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      clear();
      drawingRef.current = false;
      pointsRef.current = [];
      sweepRef.current = {
        total: 0,
        last: 0
      };
      setAccuracy(null);
      setStatus("idle");
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const reset = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-textSecondary" />;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== end || $[18] !== move) {
    t14 = <canvas ref={canvasRef} role="application" aria-label="Drawing area: press and sweep all the way around the centre dot to draw a circle" className="absolute inset-0 h-full w-full cursor-crosshair touch-none" onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} />;
    $[17] = end;
    $[18] = move;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== status) {
    t15 = status === "idle" && <p className="pointer-events-none absolute inset-x-0 bottom-5 text-center text-sm text-textGray">Press and drag all the way around the dot</p>;
    $[20] = status;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t14 || $[23] !== t15) {
    t16 = <div className="relative aspect-square w-full max-w-[440px] overflow-hidden rounded-2xl border border-ds-border-standard bg-background-secondary">{t13}{t14}{t15}</div>;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const t17 = status === "done" ? accuracy : null;
  const t18 = status === "tooShort";
  let t19;
  if ($[25] !== best || $[26] !== t17 || $[27] !== t18) {
    t19 = <CircleResult accuracy={t17} incomplete={t18} best={best} onRetry={reset} />;
    $[25] = best;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t16 || $[30] !== t19) {
    t20 = <div className="flex flex-col items-center">{t16}{t19}</div>;
    $[29] = t16;
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  return t20;
};
export default PerfectCircle;
function _temp(point) {
  return point.radius;
}
function _temp2(point_0) {
  return point_0.radius;
}